import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, Calendar, CreditCard, FileText, MessageSquare, Shield, Users, ArrowRight } from "lucide-react";
import AppShell, { getActiveSchemeId } from "@/components/AppShell";
import { useAuth } from "@/auth/AuthContext";
import { api, apiError } from "@/lib/api";
import ChairAttention from "@/pages/cockpits/ChairAttention";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scheme, setScheme] = useState(null);
  const [members, setMembers] = useState([]);
  const [lots, setLots] = useState([]);
  const [summary, setSummary] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [insurance, setInsurance] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Resolve active scheme — prefer localStorage, otherwise pick first from /api/schemes
        let id = getActiveSchemeId();
        if (!id) {
          const { data: list } = await api.get("/schemes");
          if (!list.length) {
            // Platform admin without a scheme → land on admin console
            navigate(user?.system_role === "platform_admin" ? "/admin/compliance/templates" : "/onboarding", { replace: true });
            return;
          }
          id = list[0].scheme_id;
          localStorage.setItem("opstrai.active_scheme_id", id);
        }
        const [s, m, l, sum, mtg, ins] = await Promise.all([
          api.get(`/schemes/${id}`),
          api.get(`/schemes/${id}/members`),
          api.get(`/schemes/${id}/lots`),
          api.get(`/schemes/${id}/compliance/summary`),
          api.get(`/schemes/${id}/meetings`),
          api.get(`/schemes/${id}/insurance`),
        ]);
        if (!mounted) return;
        // Owner-only members get redirected to the dedicated portal landing
        const myMembership = m.data.find((mm) => mm.user_id === user?.user_id);
        if (myMembership && myMembership.role === "owner" && user?.system_role !== "platform_admin") {
          navigate("/owner", { replace: true });
          return;
        }
        setScheme(s.data);
        setMembers(m.data);
        setLots(l.data);
        setSummary(sum.data);
        setMeetings(mtg.data);
        setInsurance(ins.data);
      } catch (e) {
        if (e?.response?.status === 401) {
          navigate("/login");
        } else if (e?.response?.status === 403 || e?.response?.status === 404) {
          // Stale scheme id — clear and retry from server
          localStorage.removeItem("opstrai.active_scheme_id");
          navigate(user?.system_role === "platform_admin" ? "/admin/compliance/templates" : "/onboarding");
        } else {
          setError(apiError(e));
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [navigate, user?.system_role]);

  if (error) {
    return (
      <AppShell>
        <div className="max-w-6xl mx-auto px-6 py-10">
          <div
            className="rounded-lg p-4 text-sm"
            style={{ background: "var(--alert-light)", color: "var(--alert)" }}
            data-testid="dashboard-error"
          >
            {error}
          </div>
        </div>
      </AppShell>
    );
  }

  if (!scheme) {
    return (
      <AppShell>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="flex gap-2">
            <span className="ai-dot" />
            <span className="ai-dot" />
            <span className="ai-dot" />
          </div>
        </div>
      </AppShell>
    );
  }

  const role = scheme.my_role;
  const isOwner = role === "owner";

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8 lg:py-10" data-testid="dashboard-page">
        {/* Hero */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <span
              className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] mb-2"
              style={{ color: "var(--blue-dark)" }}
            >
              <span
                className="inline-block w-1.5 h-1.5 rounded-full"
                style={{ background: "var(--blue)" }}
              />
              {role === "chair" && "Chair view"}
              {role === "committee" && "Committee view"}
              {role === "owner" && "Owner view"}
            </span>
            <h1 className="text-3xl lg:text-4xl font-bold" style={{ color: "var(--navy)" }} data-testid="dashboard-scheme-name">
              {scheme.name}
            </h1>
            <p className="mt-1 text-sm" style={{ color: "var(--slate)" }} data-testid="dashboard-scheme-address">
              {scheme.address}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Pill data-testid="dashboard-state-badge" bg="var(--blue-light)" fg="var(--blue-dark)">
              {scheme.state}
            </Pill>
            <Pill data-testid="dashboard-plan-pill">Plan {scheme.plan_number}</Pill>
            <Pill data-testid="dashboard-lots-pill">
              <Building2 size={11} /> {isOwner ? `${lots.length} of your lots` : `${scheme.total_lots} lots`}
            </Pill>
            <Pill data-testid="dashboard-members-pill">
              <Users size={11} /> {members.length} members
            </Pill>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <Stat label={isOwner ? "Your lots" : "Total lots"} value={isOwner ? lots.length : scheme.total_lots} />
          <Stat label="Active members" value={members.length} />
          <Stat label="State" value={scheme.state} mono />
          <Stat label="Plan" value={scheme.plan_number} mono />
        </div>

        {/* Home Pages Rework — chair/committee attention cockpit */}
        {(role === "chair" || role === "committee") && (
          <ChairAttention schemeId={scheme.scheme_id} />
        )}

        {/* Scheme Health + Next Meeting — Phase 2 + 3 */}
        {summary && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <SchemeHealthWidget summary={summary} schemeId={scheme.scheme_id} state={scheme.state} />
            </div>
            <NextMeetingWidget meetings={meetings} schemeId={scheme.scheme_id} />
          </div>
        )}

        {/* Phase-locked cards */}
        <h2 className="text-base font-bold mb-3 mt-10" style={{ color: "var(--navy)" }}>
          Coming next
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <BillingDashWidget />
          <OwnerPortalDashWidget schemeId={scheme?.scheme_id} />
          {insurance.length > 0 ? (
            <InsuranceDashWidget policies={insurance} />
          ) : (
            <PhaseCard
              phase="Phase 5"
              title="Insurance"
              description="Add a policy to see live renewal countdowns and AI adequacy checks."
              icon={<Shield size={16} />}
              testid="phase-card-insurance"
              href="/scheme/insurance"
              badge="Live"
            />
          )}
        </div>

        {/* Quick demo callout */}
        <div
          className="mt-10 rounded-xl border p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white"
          style={{ borderColor: "var(--border)" }}
        >
          <div>
            <h3 className="text-sm font-bold mb-0.5" style={{ color: "var(--navy)" }}>
              Try the AI tools today
            </h3>
            <p className="text-xs" style={{ color: "var(--slate)" }}>
              The Phase 0 demos are still live — generate AGM minutes or ask an owner question, no login needed.
            </p>
          </div>
          <Link
            to="/demo"
            className="text-sm font-semibold inline-flex items-center gap-1.5 px-4 py-2 rounded-md border hover:bg-slate-50"
            style={{ borderColor: "var(--border)", color: "var(--blue-dark)" }}
            data-testid="dashboard-demo-link"
          >
            Open demo tools →
          </Link>
        </div>
      </div>
    </AppShell>
  );
}

function Pill({ children, bg, fg, ...rest }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border"
      style={{
        borderColor: "var(--border)",
        background: bg || "white",
        color: fg || "var(--slate)",
      }}
      {...rest}
    >
      {children}
    </span>
  );
}

function Stat({ label, value, mono = false }) {
  return (
    <div
      className="rounded-lg border bg-white px-4 py-3"
      style={{ borderColor: "var(--border)" }}
    >
      <div
        className="text-[10px] font-semibold uppercase tracking-wider"
        style={{ color: "var(--tagline-grey)" }}
      >
        {label}
      </div>
      <div
        className={mono ? "mono text-xl font-bold mt-0.5" : "text-xl font-bold mt-0.5"}
        style={{ color: "var(--navy)" }}
      >
        {value}
      </div>
    </div>
  );
}

function SchemeHealthWidget({ summary, schemeId, state }) {
  const score = summary.health_score;
  const band = summary.health_band; // "green" | "amber" | "red"
  const palette = {
    green: { color: "var(--success)", bg: "var(--success-light)", label: "Healthy" },
    amber: { color: "var(--warning)", bg: "var(--warning-light)", label: "Needs attention" },
    red:   { color: "var(--alert)",   bg: "var(--alert-light)",   label: "At risk" },
  }[band];

  // SVG gauge geometry — half-circle arc
  const R = 70;
  const C = Math.PI * R; // arc length
  const dash = (score / 100) * C;
  const stroke = palette.color;

  return (
    <div
      className="rounded-2xl border bg-white p-5 lg:p-6"
      style={{ borderColor: "var(--border)" }}
      data-testid="scheme-health-widget"
    >
      <div className="flex flex-col lg:flex-row gap-6 lg:items-center">
        {/* Gauge */}
        <div className="flex items-center gap-5">
          <div className="relative" style={{ width: 160, height: 95 }} data-testid="health-gauge">
            <svg viewBox="0 0 180 100" width="160" height="95">
              <path
                d="M 20 90 A 70 70 0 0 1 160 90"
                fill="none"
                stroke="var(--slate-light)"
                strokeWidth="14"
                strokeLinecap="round"
              />
              <path
                d="M 20 90 A 70 70 0 0 1 160 90"
                fill="none"
                stroke={stroke}
                strokeWidth="14"
                strokeLinecap="round"
                strokeDasharray={`${dash} ${C}`}
                style={{ transition: "stroke-dasharray 600ms ease-out" }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-end pb-1">
              <div className="text-3xl font-bold tabular-nums" style={{ color: "var(--navy)" }} data-testid="health-score-value">
                {score}
              </div>
              <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--tagline-grey)" }}>
                out of 100
              </div>
            </div>
          </div>
          <div>
            <div
              className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
              style={{ background: palette.bg, color: palette.color }}
              data-testid={`health-band-${band}`}
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: palette.color }} />
              {palette.label}
            </div>
            <h3 className="text-lg font-bold mt-2" style={{ color: "var(--navy)" }}>Scheme health</h3>
            <p className="text-xs mt-1 max-w-[260px]" style={{ color: "var(--slate)" }} data-testid="health-explanation">
              {summary.health_explanation}
            </p>
          </div>
        </div>

        {/* Counts */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 flex-1">
          <CountTile label="Overdue" value={summary.overdue} color="var(--alert)" testid="health-overdue" />
          <CountTile label="Due soon" value={summary.due_soon} color="var(--warning)" testid="health-due-soon" />
          <CountTile label="Upcoming" value={summary.upcoming} color="var(--slate)" testid="health-upcoming" />
          <CountTile label="Done YTD" value={summary.completed_this_year} color="var(--success)" testid="health-completed" />
        </div>
      </div>

      {/* Category breakdown */}
      {summary.category_breakdown?.length > 0 && (
        <div className="mt-5 pt-4 border-t" style={{ borderColor: "var(--border)" }}>
          <div className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--slate)" }}>
            By category — {state}
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="health-category-breakdown">
            {summary.category_breakdown.map((c) => (
              <CategoryChip key={c.category} c={c} />
            ))}
          </div>
        </div>
      )}

      {/* Top attention + CTA */}
      <div className="mt-5 pt-4 border-t flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" style={{ borderColor: "var(--border)" }}>
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Shield size={14} style={{ color: "var(--blue)" }} />
          {summary.top_attention?.length > 0 ? (
            <span className="text-xs truncate" style={{ color: "var(--slate)" }} data-testid="health-top-attention">
              Top attention: <strong style={{ color: "var(--navy)" }}>{summary.top_attention[0].title}</strong> — due {summary.top_attention[0].due_date}
            </span>
          ) : (
            <span className="text-xs" style={{ color: "var(--tagline-grey)" }}>
              Nothing requires immediate attention.
            </span>
          )}
        </div>
        <Link
          to="/scheme/compliance"
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border whitespace-nowrap hover:bg-slate-50"
          style={{ borderColor: "var(--border)", color: "var(--blue-dark)" }}
          data-testid="health-open-radar"
        >
          Open Compliance Radar <ArrowRight size={12} />
        </Link>
      </div>
    </div>
  );
}

function CountTile({ label, value, color, testid }) {
  return (
    <div
      className="rounded-lg border bg-white px-3 py-2.5 text-center"
      style={{ borderColor: "var(--border)" }}
      data-testid={testid}
    >
      <div className="text-2xl font-bold tabular-nums" style={{ color }}>{value}</div>
      <div className="text-[10px] font-semibold uppercase tracking-wider mt-0.5" style={{ color: "var(--tagline-grey)" }}>{label}</div>
    </div>
  );
}

function CategoryChip({ c }) {
  const hasIssue = c.overdue > 0 || c.due_soon > 0;
  const dot = c.overdue > 0 ? "var(--alert)" : c.due_soon > 0 ? "var(--warning)" : "var(--success)";
  const tooltip = `${c.category}: ${c.overdue} overdue, ${c.due_soon} due soon, ${c.open} open, ${c.completed_this_year} done YTD`;
  return (
    <span
      title={tooltip}
      className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-full border"
      style={{ borderColor: "var(--border)", background: "white", color: hasIssue ? "var(--navy)" : "var(--slate)" }}
      data-testid={`category-chip-${c.category}`}
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: dot }} />
      <span className="capitalize">{c.category}</span>
      <span className="mono text-[10px]" style={{ color: "var(--tagline-grey)" }}>
        {c.overdue + c.due_soon}/{c.open}
      </span>
    </span>
  );
}

function NextMeetingWidget({ meetings, schemeId }) {
  const now = Date.now();
  const upcoming = meetings
    .filter((m) => ["draft", "notice_sent"].includes(m.status))
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  const inProgress = meetings.filter((m) => m.status === "in_progress");
  const next = inProgress[0] || upcoming[0];
  const upcomingCount = upcoming.length;
  const inProgressCount = inProgress.length;

  return (
    <div
      className="rounded-2xl border bg-white p-5 lg:p-6 flex flex-col"
      style={{ borderColor: "var(--border)" }}
      data-testid="next-meeting-widget"
    >
      <div className="flex items-center gap-2 mb-3">
        <Calendar size={14} style={{ color: "var(--blue)" }} />
        <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: "var(--navy)" }}>
          Meetings
        </h3>
      </div>
      <div className="flex items-baseline gap-4 mb-4">
        <div>
          <div className="text-2xl font-bold tabular-nums" style={{ color: "var(--navy)" }} data-testid="meetings-in-progress-count">{inProgressCount}</div>
          <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--tagline-grey)" }}>in progress</div>
        </div>
        <div>
          <div className="text-2xl font-bold tabular-nums" style={{ color: "var(--navy)" }} data-testid="meetings-upcoming-count">{upcomingCount}</div>
          <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--tagline-grey)" }}>upcoming</div>
        </div>
      </div>
      {next ? (
        <Link
          to={`/scheme/meetings/${next.meeting_id}`}
          className="flex-1 -mx-2 -mb-2 px-3 py-2.5 rounded-lg border hover:bg-slate-50 transition-colors"
          style={{ borderColor: "var(--border)" }}
          data-testid="next-meeting-card"
        >
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "var(--blue-light)", color: "var(--blue-dark)" }}>
              {next.meeting_type}
            </span>
            <span className="text-[10px]" style={{ color: "var(--tagline-grey)" }}>Next</span>
          </div>
          <div className="text-sm font-semibold truncate" style={{ color: "var(--navy)" }}>{next.title}</div>
          <div className="text-xs mt-0.5" style={{ color: "var(--slate)" }}>
            {new Date(next.scheduled_at).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}{" "}
            · {next.motion_count} motion{next.motion_count === 1 ? "" : "s"}
          </div>
        </Link>
      ) : (
        <Link to="/scheme/meetings" className="flex-1 flex items-center justify-center text-xs hover:underline" style={{ color: "var(--slate)" }} data-testid="no-next-meeting">
          No meetings yet — schedule one →
        </Link>
      )}
    </div>
  );
}

function OwnerPortalDashWidget({ schemeId }) {
  const [docs, setDocs] = useState(0);
  const [outstanding, setOutstanding] = useState(0);
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (!schemeId) return;
    (async () => {
      try {
        const { api } = await import("@/lib/api");
        const [d, l, t] = await Promise.allSettled([
          api.get(`/schemes/${schemeId}/documents`),
          api.get(`/schemes/${schemeId}/levies`),
          api.get(`/schemes/${schemeId}/threads`),
        ]);
        if (d.status === "fulfilled") setDocs(Array.isArray(d.value.data) ? d.value.data.length : (d.value.data?.documents || []).length);
        if (l.status === "fulfilled") {
          const rows = Array.isArray(l.value.data) ? l.value.data : (l.value.data?.levies || []);
          const out = rows.filter((x) => x.status !== "paid").reduce((s, x) => s + (x.total_amount || 0), 0);
          setOutstanding(out);
        }
        if (t.status === "fulfilled") {
          const threads = Array.isArray(t.value.data) ? t.value.data : (t.value.data?.threads || []);
          setUnread(threads.reduce((s, x) => s + (x.unread_count || 0), 0));
        }
      } catch { /* non-critical */ }
    })();
  }, [schemeId]);
  return (
    <Link to="/owner" className="rounded-2xl border bg-white p-5 hover:shadow-sm transition-shadow flex flex-col" style={{ borderColor: "var(--border)" }} data-testid="owner-portal-widget">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare size={14} style={{ color: "var(--blue)" }} />
        <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: "var(--navy)" }}>Owner portal</h3>
        <span className="ml-auto text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "var(--success-light)", color: "var(--success)" }}>Live</span>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <DashTile label="Documents" value={docs} color="var(--navy)" testid="op-w-docs"/>
        <DashTile label="Outstanding" value={outstanding > 0 ? `$${outstanding.toFixed(0)}` : "$0"} color={outstanding > 0 ? "var(--warning)" : "var(--success)"} testid="op-w-outstanding"/>
        <DashTile label="Unread" value={unread} color={unread > 0 ? "var(--blue-dark)" : "var(--slate)"} testid="op-w-unread"/>
      </div>
      <div className="text-xs pb-2" style={{ color: "var(--slate)" }}>
        Plain-English Q&A, levy notices, and committee messaging — scoped to this scheme.
      </div>
      <div className="text-[11px] mt-auto pt-2 border-t flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
        <span style={{ color: "var(--slate)" }}>Open Owner Portal</span>
        <span className="text-[11px] font-semibold inline-flex items-center gap-1" style={{ color: "var(--blue-dark)" }}>Open <ArrowRight size={11}/></span>
      </div>
    </Link>
  );
}

function BillingDashWidget() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const sid = (typeof window !== "undefined") ? localStorage.getItem("opstrai.active_scheme_id") : null;
    if (!sid) return;
    (async () => {
      try {
        const { api } = await import("@/lib/api");
        const { data } = await api.get(`/schemes/${sid}/billing`);
        setData(data);
      } catch (e) { /* widget is non-critical */ }
    })();
  }, []);
  const sub = data?.subscription;
  const status = sub?.status || "not_configured";
  const pillCls = {
    active: { c: "var(--success)", bg: "var(--success-light)", label: "Active" },
    trialing: { c: "var(--blue-dark)", bg: "#dbeafe", label: "Trialing" },
    past_due: { c: "var(--warning)", bg: "#fef3c7", label: "Past due" },
    grace_period: { c: "var(--warning)", bg: "#fef3c7", label: "Grace" },
    unpaid: { c: "var(--alert)", bg: "#fee2e2", label: "Unpaid" },
    canceled: { c: "var(--slate)", bg: "var(--slate-light)", label: "Canceled" },
    incomplete: { c: "var(--slate)", bg: "var(--slate-light)", label: "Incomplete" },
    not_configured: { c: "var(--slate)", bg: "var(--slate-light)", label: "Not subscribed" },
  }[status] || { c: "var(--slate)", bg: "var(--slate-light)", label: status };
  const monthly = data ? `$${(data.monthly_total_cents / 100).toFixed(2)}` : "—";
  return (
    <Link to="/scheme/billing" className="rounded-2xl border bg-white p-5 hover:shadow-sm transition-shadow flex flex-col" style={{ borderColor: "var(--border)" }} data-testid="billing-widget">
      <div className="flex items-center gap-2 mb-3">
        <CreditCard size={14} style={{ color: "var(--blue)" }}/>
        <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: "var(--navy)" }}>Billing</h3>
        <span className="ml-auto text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: pillCls.bg, color: pillCls.c }} data-testid="billing-widget-status">{pillCls.label}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <DashTile label="Lots" value={data?.lot_count ?? "—"} color="var(--navy)" testid="billing-w-lots"/>
        <DashTile label="Monthly" value={monthly} color="var(--blue-dark)" testid="billing-w-monthly"/>
      </div>
      <div className="text-xs pb-2" style={{ color: "var(--slate)" }} data-testid="billing-w-next">
        {sub?.current_period_end ? (
          <>Next invoice: <strong style={{ color: "var(--navy)" }}>{new Date(sub.current_period_end).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</strong></>
        ) : (
          <>14-day free trial · $5 AUD per lot</>
        )}
      </div>
      <div className="text-[11px] mt-auto pt-2 border-t flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
        <span style={{ color: "var(--slate)" }}>Stripe {data?.stripe_configured ? "ready" : "not configured"}</span>
        <span className="text-[11px] font-semibold inline-flex items-center gap-1" style={{ color: "var(--blue-dark)" }}>Open <ArrowRight size={11}/></span>
      </div>
    </Link>
  );
}

function InsuranceDashWidget({ policies }) {
  const active = policies.filter((p) => p.status === "active").length;
  const renewing = policies.filter((p) => p.status === "renewing_soon").length;
  const expired = policies.filter((p) => p.status === "expired").length;
  // Next renewal — earliest period_end among active/renewing
  const sorted = [...policies]
    .filter((p) => p.status !== "expired")
    .sort((a, b) => new Date(a.period_end) - new Date(b.period_end));
  const next = sorted[0];
  const days = next ? Math.round((new Date(next.period_end) - Date.now()) / 86400000) : null;
  const daysColor = days == null ? "var(--slate)" : days < 0 ? "var(--alert)" : days < 30 ? "var(--alert)" : days < 60 ? "var(--warning)" : "var(--success)";
  // Risk pulse: latest_adequacy_check in last 90d with concerning band
  const cutoff = Date.now() - 90 * 86400000;
  const concerning = policies.filter((p) => {
    const c = p.latest_adequacy_check;
    if (!c) return false;
    if (new Date(c.ran_at).getTime() < cutoff) return false;
    return c.band === "likely_under" || c.band === "insufficient_data";
  }).length;
  return (
    <Link to="/scheme/insurance" className="rounded-2xl border bg-white p-5 hover:shadow-sm transition-shadow flex flex-col" style={{ borderColor: "var(--border)" }} data-testid="insurance-widget">
      <div className="flex items-center gap-2 mb-3">
        <Shield size={14} style={{ color: "var(--blue)" }}/>
        <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: "var(--navy)" }}>Insurance</h3>
        <span className="ml-auto text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "var(--success-light)", color: "var(--success)" }}>Live</span>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <DashTile label="Active" value={active} color="var(--success)" testid="ins-w-active"/>
        <DashTile label="Renewing" value={renewing} color="var(--warning)" testid="ins-w-renewing"/>
        <DashTile label="Expired" value={expired} color={expired > 0 ? "var(--alert)" : "var(--slate)"} testid="ins-w-expired"/>
      </div>
      {next && (
        <div className="text-xs pb-2" style={{ color: "var(--slate)" }} data-testid="ins-w-next">
          Next renewal: <strong style={{ color: "var(--navy)" }}>{next.policy_type.replace(/_/g, " ")}</strong>
          {" "}— <span className="font-mono" style={{ color: daysColor }}>{days < 0 ? `${-days}d ago` : `in ${days}d`}</span>
        </div>
      )}
      <div className="text-[11px] mt-auto pt-2 border-t flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
        <span style={{ color: "var(--slate)" }} data-testid="ins-w-pulse">
          Risk pulse: <strong style={{ color: concerning > 0 ? "var(--warning)" : "var(--success)" }}>{concerning}</strong> flagged (90d)
        </span>
        <span className="text-[11px] font-semibold inline-flex items-center gap-1" style={{ color: "var(--blue-dark)" }}>Open <ArrowRight size={11}/></span>
      </div>
    </Link>
  );
}

function DashTile({ label, value, color, testid }) {
  return (
    <div className="text-center" data-testid={testid}>
      <div className="text-2xl font-bold tabular-nums" style={{ color }}>{value}</div>
      <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--tagline-grey)" }}>{label}</div>
    </div>
  );
}

function PhaseCard({ phase, title, description, icon, testid, href, badge }) {
  const inner = (
    <div
      className="rounded-xl border bg-white p-5 h-full"
      style={{ borderColor: "var(--border)" }}
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-3">
        <div
          className="inline-flex items-center justify-center rounded-md"
          style={{
            width: 32,
            height: 32,
            background: "var(--slate-light)",
            color: "var(--slate)",
          }}
        >
          {icon}
        </div>
        <span
          className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
          style={{ background: "var(--blue-light)", color: "var(--blue-dark)" }}
        >
          {badge || phase}
        </span>
      </div>
      <h3 className="text-sm font-bold mb-1" style={{ color: "var(--navy)" }}>
        {title}
      </h3>
      <p className="text-xs leading-relaxed mb-3" style={{ color: "var(--slate)" }}>
        {description}
      </p>
      <span
        className="text-[11px] italic"
        style={{ color: href ? "var(--blue)" : "var(--tagline-grey)" }}
      >
        {href ? "Open →" : "Coming soon"}
      </span>
    </div>
  );
  return href ? <Link to={href}>{inner}</Link> : inner;
}
